import React, { memo, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ModalHeader from '../../../shared/components/ModalHeader';
import { useViewport } from '../../../shared/hooks/useViewport';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import WorkflowRunRow from './WorkflowRunRow';
import WorkflowRunModal from './WorkflowRunModal';
import { useCustomerWorkflowRun, useCustomerWorkflowRuns } from '../hooks/useCustomerWorkflowRuns';
import type { WorkflowRunSummary } from '../../../types/backend';
import { isRunActive } from '../runStatus';
import { safeParseDate } from '../../../shared/utils/dateUtils';

// The most recent finished runs answer what has been walked lately. The rest stay available
// without being in the way: a customer accumulates them forever, and the handful anyone wants
// is at one end of the list.
const FINISHED_SHOWN = 3;

// Newest first, by when the run last moved. An unparseable timestamp sorts last rather than
// throwing the whole order out.
const newestFirst = (
  a: Pick<WorkflowRunSummary, 'updated_at'>,
  b: Pick<WorkflowRunSummary, 'updated_at'>,
) =>
  (safeParseDate(b.updated_at)?.getTime() ?? 0) - (safeParseDate(a.updated_at)?.getTime() ?? 0);

interface Props {
  open: boolean;
  onClose: () => void;
  customerId: string | undefined;
}

// Every procedure walked for this customer, which is a different question from the one the
// investigation sidebar answers. The sidebar lists what the open case is walking; this lists what
// has been walked, including runs an automation started that no case ever picked up.
//
// Read-only on purpose. The row's Retry and Abandon bind to an investigation, and this list has
// none: offering them here would mean something different per row depending on whether a case
// happens to link that run. Acting on one is done from the case it belongs to.
//
// Opening a run shows the same roomy view the sidebar opens, with no write handlers passed, so
// the operator reads the steps and the path taken without being offered an act this surface
// cannot carry out (WF-UC-11 step 3).
const CustomerWorkflowRunsModal = memo<Props>(({ open, onClose, customerId }) => {
  const { isPhone } = useViewport();
  const { runs, loading, error, refetch } = useCustomerWorkflowRuns(open ? customerId : undefined);
  const [selected, setSelected] = useState<string | null>(null);
  const [allFinished, setAllFinished] = useState(false);
  const opened = useCustomerWorkflowRun(open ? customerId : undefined, selected);

  // Closing the history, or switching customer under it, drops the opened run. Left set, it
  // would reopen on the next visit, or ask this customer for another customer's run.
  useEffect(() => {
    setSelected(null);
    setAllFinished(false);
  }, [open, customerId]);

  // Open runs first: a run nobody is walking is the reason to open this at all, and it sorts
  // below every finished run if the list is left in the order the table returns. The heading is
  // "Still open" rather than "Still going" because one of these can be a run with nothing behind
  // it, and a heading that claimed otherwise would contradict the row beneath it.
  const active = runs.filter((r) => isRunActive(r.status)).sort(newestFirst);
  const finished = runs.filter((r) => !isRunActive(r.status)).sort(newestFirst);
  const finishedShown = allFinished ? finished : finished.slice(0, FINISHED_SHOWN);
  const finishedHidden = finished.length - finishedShown.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isPhone}>
      <ModalHeader title="Workflow runs" onClose={onClose} />
      <DialogContent dividers>
        {/* The error is read before the spinner and before the empty state: a listing that could
            not load is an unknown, and "no runs" would present it as a measured zero. */}
        {error ? (
          <Stack spacing={1.5}>
            <Alert severity="error">{error}</Alert>
            <Box>
              <Button size="small" onClick={() => void refetch()}>
                Try again
              </Button>
            </Box>
          </Stack>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : runs.length === 0 ? (
          <EmptyStateMessage
            icon={AccountTreeIcon}
            title="No workflow runs"
            description="No procedure has been walked for this customer yet."
          />
        ) : (
          <Stack spacing={2}>
            {active.length > 0 && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Still open
                </Typography>
                {active.map((run) => (
                  <WorkflowRunRow
                    key={run.run_id}
                    run={run}
                    selected={run.run_id === selected}
                    onSelect={setSelected}
                  />
                ))}
              </Box>
            )}
            {finished.length > 0 && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Finished
                </Typography>
                {finishedShown.map((run) => (
                  <WorkflowRunRow
                    key={run.run_id}
                    run={run}
                    selected={run.run_id === selected}
                    onSelect={setSelected}
                  />
                ))}
                {finishedHidden > 0 && (
                  <Button size="small" onClick={() => setAllFinished(true)}>
                    {`Show ${finishedHidden} older`}
                  </Button>
                )}
                {allFinished && finished.length > FINISHED_SHOWN && (
                  <Button size="small" onClick={() => setAllFinished(false)}>
                    Show fewer
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <WorkflowRunModal
        open={selected !== null}
        detail={opened.detail}
        loading={opened.loading}
        error={opened.error}
        onClose={() => setSelected(null)}
        noWritesBecause="another-surface"
      />
    </Dialog>
  );
});

CustomerWorkflowRunsModal.displayName = 'CustomerWorkflowRunsModal';

export default CustomerWorkflowRunsModal;
